// src/components/Header/Header.jsx
import React, { useState } from "react";
import { AppBar, Toolbar, Typography, IconButton, Menu, MenuItem, Box, Button } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import LoginIcon from "@mui/icons-material/Login";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "assets/logos/RURACTIVE_logo_horizontal.svg";
import default_theme from "assets/themes/themes";
import SideDrawer from "./Drawer";
import "./Header.css";
import { logout } from "../../redux/auth/authSlice";

const getDrawerItems = (user, isAuthenticated) => {
  const baseItems = [
    { icon: "🏠", text: "Home", link: "/" },
    { icon: "🧙‍♂️", text: "Wizard", link: "/wizard" },
    { icon: "💡", text: "Solutions", link: "/solutions" },
    { icon: "💼", text: "Workspaces", link: "/workspaces" },
    { icon: "📚", text: "Learn", link: "/learn", comingSoon: true },
    { icon: "📅", text: "Events", link: "/events", comingSoon: true },
  ];

  // Only show authenticated-only items if user is authenticated
  if (isAuthenticated) {
    // Add user management link for organization admins
    if (user?.role?.name === "admin" && user?.organization) {
      baseItems.push({ icon: "👥", text: "User Management", link: "/organization/users" });
    }

    baseItems.push({ icon: "👤", text: "Profile", link: "/profile" });
  }

  return baseItems;
};

const Header = ({ drawerOpen: controlledOpen, setDrawerOpen: controlledSetOpen }) => {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const drawerItems = getDrawerItems(user, isAuthenticated);

  // Fallback to internal state if not controlled by parent
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = typeof controlledOpen === "boolean" ? controlledOpen : uncontrolledOpen;
  const setOpen = typeof controlledSetOpen === "function" ? controlledSetOpen : setUncontrolledOpen;

  // Hide sidebar on authentication pages
  const isAuthPage = ['/login', '/register', '/forgot-password'].includes(location.pathname);
  // Show sidebar for all users except on auth pages (but items may be restricted based on auth)
  const isSidebarVisible = !isAuthPage;
  const effectiveOpen = isSidebarVisible ? open : false;

  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);
  const changeLanguage = (lng) => { i18n.changeLanguage(lng); handleCloseMenu(); };
  const toggleDrawer = () => setOpen(!open);

  return (
    <>
      <AppBar
        position="fixed"
        className="header"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          marginLeft: isSidebarVisible ? (effectiveOpen ? "240px" : "60px") : 0,
          width: isSidebarVisible
            ? effectiveOpen
              ? "calc(100% - 240px)"
              : "calc(100% - 60px)"
            : "100%",
          transition: "width 0.3s ease, margin 0.3s ease",
          "@media (max-width: 768px)": { marginLeft: 0, width: "100%" },
        }}
      >
        <Toolbar className="toolbar">
          <div className="logoContainer" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            <img src={logo} alt="Logo" className="logo" />
          </div>

          {/* <Typography sx={{p: '0% 5% 0% 0%'}} variant="h6" className="headerTitle">Decision Support Tool</Typography> */}

          <div className="headerButtonContainer">
            {!isAuthenticated && (
              <Button
                variant="outlined"
                startIcon={<LoginIcon />}
                onClick={() => navigate("/login")}
                sx={{
                  textTransform: "none",
                  color: default_theme.palette.primary.main,
                  borderColor: default_theme.palette.other.overlayBorder,
                  "&:hover": {
                    borderColor: default_theme.palette.primary.main,
                    backgroundColor: default_theme.palette.other.overlaySubtle,
                  },
                }}
              >
                Sign In
              </Button>
            )}
            {/* <IconButton
              onClick={handleOpenMenu}
              color="secondary"
              className="headerButton"
              aria-controls="language-menu"
              aria-haspopup="true"
            >
              <LanguageIcon />
            </IconButton>
            <Menu id="language-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
              <MenuItem onClick={() => changeLanguage("en")}>English</MenuItem>
              <MenuItem onClick={() => changeLanguage("fr")}>Français</MenuItem>
              <MenuItem onClick={() => changeLanguage("es")}>Español</MenuItem>
            </Menu> */}
          </div>
        </Toolbar>
      </AppBar>

      {isSidebarVisible && (
        <SideDrawer open={effectiveOpen} onToggle={toggleDrawer} items={drawerItems} />
      )}
    </>
  );
};

export default Header;
